/**
 * marriage-doc-groups.ts — Wave 69 (บริการที่ 11: เอกสารสมรส/ครอบครัว ไทย–ต่างชาติ)
 *
 * แหล่งอ้างอิงที่ตรวจสอบได้เท่านั้น:
 *   - ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5 (มาตรา 1448 เงื่อนไขอายุ, มาตรา 1457 การสมรสมีผลเมื่อจดทะเบียน,
 *     มาตรา 1459 การสมรส ณ สถานทูต/สถานกงสุลไทยในต่างประเทศ, มาตรา 1465–1469 สัญญาก่อนสมรส)
 *   - พ.ร.บ. แก้ไขเพิ่มเติมประมวลกฎหมายแพ่งและพาณิชย์ (ฉบับที่ 24) พ.ศ. 2567 ("สมรสเท่าเทียม")
 *     มีผลใช้บังคับ 22 มกราคม 2568 — เปลี่ยนถ้อยคำเป็น "บุคคลสองคน" และกำหนดอายุขั้นต่ำ 18 ปีบริบูรณ์
 *   - พ.ร.บ. การทะเบียนราษฎร และระเบียบสำนักทะเบียนกลางว่าด้วยการจดทะเบียนครอบครัว —
 *     การบันทึกฐานะแห่งครอบครัว (คร.22) กรณีจดทะเบียนสมรส/หย่าตามกฎหมายต่างประเทศ
 *   - กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — งานนิติกรณ์รับรองเอกสารและคำแปล
 *   - สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)
 *   - อนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) — สถานะของไทยจาก HCCH (src/data/apostille-status.ts)
 *
 * กติกาเนื้อหา: ไม่ระบุราคา ไม่รับประกันผล ไม่กำหนดระยะเวลาราชการเอง
 * เงื่อนไขที่ผันแปรตามหน่วยงาน/ประเทศปลายทาง ให้เขียนว่า "ผันแปร" พร้อมวิธีตรวจสอบจากต้นทาง
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface MarriageDocGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

export const MARRIAGE_APOSTILLE_TH =
  `สถานะกฎหมาย: ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) เมื่อ ` +
  `${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับประเทศไทยในวันที่ ` +
  `${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนถึงวันดังกล่าว ทะเบียนสมรส ทะเบียนหย่า และเอกสารครอบครัวของไทย ` +
  `ที่จะใช้ในต่างประเทศ ยังต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุลและสถานทูตของประเทศปลายทางตามลำดับเดิม`;

export const MARRIAGE_APOSTILLE_EN =
  `Legal status: Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on ` +
  `${APOSTILLE_STATUS.accessionDepositEn}; it enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} ` +
  `(source: HCCH). Until that date, Thai marriage certificates, divorce registrations and family-status records ` +
  `used abroad still pass through the Department of Consular Affairs and the destination embassy in the traditional order.`;

export const MARRIAGE_EQUALITY_TH =
  `พ.ร.บ. แก้ไขเพิ่มเติมประมวลกฎหมายแพ่งและพาณิชย์ (ฉบับที่ 24) พ.ศ. 2567 หรือกฎหมายสมรสเท่าเทียม ` +
  `มีผลใช้บังคับตั้งแต่วันที่ 22 มกราคม 2568 ทำให้ "บุคคลสองคน" ไม่ว่าเพศใดจดทะเบียนสมรสตามกฎหมายไทยได้ ` +
  `และกำหนดอายุขั้นต่ำของคู่สมรสไว้ที่ 18 ปีบริบูรณ์`;

export const MARRIAGE_EQUALITY_EN =
  `The Civil and Commercial Code Amendment Act (No. 24) B.E. 2567 — Thailand's marriage-equality law — ` +
  `took effect on 22 January 2025. Any two persons, regardless of gender, may register a marriage under Thai law, ` +
  `and the minimum age for both parties is 18.`;

export const MARRIAGE_DOC_GROUPS: MarriageDocGroup[] = [
  {
    slug: "affirmation-freedom-to-marry",
    titleTh: "หนังสือรับรองสถานภาพ / รับรองความเป็นโสดจากสถานทูต",
    titleEn: "Affirmation of freedom to marry from the embassy",
    summaryTh:
      "เอกสารตั้งต้นของคู่สมรสชาวต่างชาติ: สถานทูตหรือสถานกงสุลของตนออกหนังสือรับรองว่ามีอิสระที่จะสมรส แล้วนำมาแปลเป็นไทย รับรองคำแปลที่กรมการกงสุล ก่อนยื่นจดทะเบียนที่สำนักทะเบียนอำเภอหรือเขต",
    summaryEn:
      "The foreign partner's starting document: the home embassy or consulate issues an affirmation of freedom to marry, which is then translated into Thai and legalised at the Department of Consular Affairs before the district office will register the marriage.",
    bodyTh: [
      "สำนักทะเบียนอำเภอและสำนักงานเขตในประเทศไทยไม่มีวิธีตรวจสอบสถานภาพการสมรสของชาวต่างชาติจากฐานข้อมูลไทยได้เอง จึงอาศัยหนังสือรับรองที่ออกโดยสถานทูตหรือสถานกงสุลของประเทศนั้นในไทย ชื่อเรียกเอกสารต่างกันไปตามประเทศ เช่น Affirmation of Freedom to Marry, Certificate of No Impediment, Single Status Certificate หรือ Nulla Osta แต่หน้าที่ทางกฎหมายเหมือนกันคือยืนยันว่าผู้ยื่นไม่มีคู่สมรสอยู่ก่อน",
      "ขั้นตอนการขอเอกสารนี้เป็นระเบียบภายในของแต่ละสถานทูต บางแห่งออกให้ทันทีเมื่อยื่นคำสาบานพร้อมหนังสือเดินทาง บางแห่งต้องนัดหมายล่วงหน้า บางแห่งกำหนดให้ยื่นหลักฐานการหย่าหรือใบมรณบัตรของคู่สมรสเดิมประกอบ เราจึงไม่ระบุขั้นตอนตายตัวแทนสถานทูต แต่จะช่วยตรวจรายการเอกสารกับประกาศล่าสุดของสถานทูตนั้นก่อนวันนัด",
      "เมื่อได้เอกสารมาแล้ว ต้องแปลเป็นภาษาไทยและนำไปรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ นายทะเบียนจะพิจารณาจากคำแปลภาษาไทยที่ผ่านการรับรองแล้วเท่านั้น ต้นฉบับภาษาต่างประเทศอย่างเดียวไม่พอสำหรับการจดทะเบียน",
      "ตัวสะกดชื่อคือจุดที่ทำให้เรื่องตกบ่อยที่สุด ชื่อในหนังสือรับรองต้องตรงกับหนังสือเดินทางทุกตัวอักษร และคำแปลภาษาไทยต้องถอดเสียงให้สอดคล้องกันตลอดทั้งชุดเอกสาร หากเคยเปลี่ยนชื่อหรือเคยสมรสมาก่อน ต้องแนบหลักฐานให้ครบเพื่อให้สายเอกสารเชื่อมโยงกันได้",
      "อายุการใช้งานของหนังสือรับรองเป็นดุลพินิจของนายทะเบียนและผันแปรตามแนวปฏิบัติของแต่ละสำนักทะเบียน ผู้ยื่นจึงไม่ควรขอเอกสารล่วงหน้านานเกินไปโดยไม่ได้สอบถามสำนักทะเบียนที่จะไปจดทะเบียนไว้ก่อน",
    ],
    bodyEn: [
      "Thai district offices have no way to verify a foreigner's marital status from Thai records, so they rely on a certificate issued by that person's embassy or consulate in Thailand. The name varies by country — Affirmation of Freedom to Marry, Certificate of No Impediment, Single Status Certificate, Nulla Osta — but the legal function is identical: confirming there is no subsisting marriage.",
      "How the document is obtained is entirely the embassy's internal procedure. Some issue it on the spot against a sworn declaration and passport, others require an appointment, and several ask for a divorce decree or a former spouse's death certificate first. We do not publish a fixed procedure on the embassy's behalf; we check your list against that embassy's current published requirements before your appointment.",
      "Once issued, the affirmation must be translated into Thai and the translation legalised by the Legalisation Division of the Department of Consular Affairs. The registrar decides on the legalised Thai translation — a foreign-language original alone is not sufficient for registration.",
      "Name spelling is where most files fail. The name on the affirmation must match the passport character for character, and the Thai transliteration must stay consistent across the whole bundle. Anyone who has changed name or been married before must attach the supporting evidence so the paper trail connects.",
      "How long the affirmation stays acceptable is at the registrar's discretion and varies between district offices. Do not obtain it far in advance without first asking the specific office where you intend to register.",
    ],
    documentsTh: [
      "หนังสือเดินทางของคู่สมรสชาวต่างชาติ (ตัวจริง + สำเนาหน้าข้อมูล)",
      "หนังสือรับรองสถานภาพ/ความเป็นโสด ออกโดยสถานทูตหรือสถานกงสุลของประเทศตน",
      "คำแปลภาษาไทยของหนังสือรับรอง ผ่านการรับรองที่กรมการกงสุล",
      "หลักฐานการสิ้นสุดการสมรสเดิม เช่น ทะเบียนหย่า คำพิพากษา หรือใบมรณบัตร (ถ้ามี)",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านของคู่สมรสฝ่ายไทย",
      "หลักฐานการเปลี่ยนชื่อ–นามสกุล (ถ้ามี) ของทั้งสองฝ่าย",
    ],
    documentsEn: [
      "Foreign partner's passport (original plus a copy of the bio page)",
      "Affirmation of freedom to marry / certificate of no impediment from the home embassy or consulate",
      "Thai translation of that affirmation, legalised by the Department of Consular Affairs",
      "Evidence that any earlier marriage ended: divorce registration, court judgment or death certificate (if applicable)",
      "Thai partner's national ID card and house registration",
      "Change-of-name evidence for either party (if applicable)",
    ],
    chainTh: [
      "ขอหนังสือรับรองสถานภาพจากสถานทูต/สถานกงสุลของคู่สมรสชาวต่างชาติในประเทศไทย",
      "แปลหนังสือรับรองเป็นภาษาไทยโดยผู้แปลที่รับผิดชอบคำแปล",
      "ยื่นรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
      "นำชุดเอกสารพร้อมพยาน 2 คน ยื่นจดทะเบียนสมรสที่สำนักทะเบียนอำเภอหรือสำนักงานเขตใดก็ได้",
      "ขอคัดสำเนาทะเบียนสมรส (คร.2) และใบสำคัญการสมรส (คร.3) เพื่อใช้ขั้นตอนต่อไป",
    ],
    chainEn: [
      "Obtain the affirmation of freedom to marry from the foreign partner's embassy or consulate in Thailand",
      "Have it translated into Thai by a translator who takes responsibility for the translation",
      "Submit the translation for legalisation at the Legalisation Division, Department of Consular Affairs",
      "Register the marriage with two witnesses at any district office (amphur) or khet office",
      "Request certified copies of the marriage register (Kor.Ror. 2) and marriage certificate (Kor.Ror. 3) for the next steps",
    ],
    checklistTh: [
      "ตัวสะกดชื่อภาษาอังกฤษในทุกฉบับตรงกับหนังสือเดินทาง",
      "คำแปลภาษาไทยผ่านการรับรองจากกรมการกงสุลแล้ว",
      "เตรียมพยานบุคคล 2 คนพร้อมบัตรประจำตัว",
      "ตรวจกับสำนักทะเบียนที่จะไปยื่นว่าต้องการเอกสารเพิ่มหรือไม่",
      "ทั้งสองฝ่ายอายุครบ 18 ปีบริบูรณ์ตามกฎหมายที่ใช้บังคับปัจจุบัน",
    ],
    checklistEn: [
      "Latin-script names match the passport on every document",
      "Thai translation already legalised by the Department of Consular Affairs",
      "Two witnesses with ID available on the day",
      "Confirmed with the specific district office whether it wants anything extra",
      "Both parties are at least 18, as required under the law now in force",
    ],
    faqsTh: [
      {
        q: "จดทะเบียนสมรสที่อำเภอไหนก็ได้หรือไม่",
        a: "การจดทะเบียนสมรสยื่นได้ที่สำนักทะเบียนอำเภอหรือสำนักงานเขตใดก็ได้ทั่วประเทศ ไม่ผูกกับทะเบียนบ้าน แต่แต่ละสำนักทะเบียนอาจมีแนวปฏิบัติเรื่องคิว การนัดหมาย และล่ามต่างกัน ควรสอบถามล่วงหน้า",
      },
      {
        q: "ต้องใช้ล่ามในวันจดทะเบียนหรือไม่",
        a: "หากคู่สมรสชาวต่างชาติไม่เข้าใจภาษาไทย นายทะเบียนอาจกำหนดให้มีล่ามเพื่อยืนยันว่าเข้าใจข้อความที่ลงนาม เป็นดุลพินิจของแต่ละสำนักทะเบียน จึงควรสอบถามก่อนวันนัด",
      },
      {
        q: "หนังสือรับรองความเป็นโสดจากประเทศบ้านเกิด ใช้แทนของสถานทูตในไทยได้ไหม",
        a: "ขึ้นอยู่กับสำนักทะเบียนและประเทศต้นทาง โดยทั่วไปเอกสารที่ออกในต่างประเทศต้องผ่านการรับรองตามระเบียบของประเทศนั้นและรับรองที่สถานทูตไทย ก่อนนำมาแปลและรับรองคำแปลที่กรมการกงสุลอีกชั้น",
      },
      {
        q: "คู่รักเพศเดียวกันจดทะเบียนสมรสในไทยได้แล้วหรือยัง",
        a: "ได้ กฎหมายสมรสเท่าเทียม (แก้ไขประมวลกฎหมายแพ่งและพาณิชย์ ฉบับที่ 24 พ.ศ. 2567) มีผลใช้บังคับตั้งแต่ 22 มกราคม 2568 บุคคลสองคนไม่ว่าเพศใดจดทะเบียนสมรสตามกฎหมายไทยได้",
      },
      {
        q: "ค่าบริการเท่าไร",
        a: "เอกสารแต่ละชุดต่างกันตามจำนวนหน้า ภาษา และขั้นตอนรับรองที่ต้องใช้ กรุณาสอบถามเจ้าหน้าที่ทางโทรศัพท์ LINE หรืออีเมล เพื่อรับข้อมูลที่ตรงกับเคสของคุณ",
      },
    ],
    faqsEn: [
      {
        q: "Can we register at any district office?",
        a: "Yes. A marriage may be registered at any amphur or khet office in Thailand regardless of where either party is registered as resident. Queueing, appointment and interpreter practice differ between offices, so call ahead.",
      },
      {
        q: "Do we need an interpreter on the day?",
        a: "If the foreign partner does not understand Thai, the registrar may require an interpreter to confirm the party understands what is being signed. This is at each office's discretion — ask before the appointment.",
      },
      {
        q: "Can a single-status certificate issued back home replace the embassy one?",
        a: "A single-status certificate issued in your home country can sometimes replace an embassy certificate depending on the specific district office and issuing nation. Documents issued abroad typically require authentication under that country's regulations, legalization by the Royal Thai Embassy, certified Thai translation, and final legalization by the Department of Consular Affairs.",
      },
      {
        q: "Can same-sex couples marry in Thailand?",
        a: "Yes. The marriage-equality amendment to the Civil and Commercial Code (Act No. 24, B.E. 2567) came into force on 22 January 2025, allowing any two persons to register a marriage under Thai law.",
      },
      {
        q: "How much does it cost?",
        a: "Every file differs by page count, language pair and which certification steps apply. Please contact our team by phone, LINE or email for a figure that matches your documents.",
      },
    ],
  },
  {
    slug: "thai-marriage-certificate-abroad",
    titleTh: "ทะเบียนสมรสไทยที่จะใช้ในต่างประเทศ",
    titleEn: "Thai marriage certificate for use abroad",
    summaryTh:
      "คู่สมรสที่จดทะเบียนในไทยแล้วต้องนำ คร.2/คร.3 ไปใช้กับหน่วยงานต่างประเทศ ต้องแปลเป็นภาษาปลายทาง รับรองคำแปลที่กรมการกงสุล และรับรองต่อที่สถานทูตของประเทศนั้น",
    summaryEn:
      "Couples already married in Thailand who must present the Kor.Ror. 2 / Kor.Ror. 3 to a foreign authority: translate into the destination language, legalise the translation at the Department of Consular Affairs, then have the destination embassy endorse it.",
    bodyTh: [
      "ทะเบียนสมรสของไทยมีสองฉบับที่ใช้บ่อยและมีน้ำหนักต่างกัน คือ ใบสำคัญการสมรส (คร.3) ซึ่งเป็นใบมอบให้คู่สมรสถือไว้ และสำเนาทะเบียนสมรส (คร.2) ซึ่งเป็นสำเนารายการทะเบียนที่นายทะเบียนรับรอง หน่วยงานต่างประเทศจำนวนมากขอ คร.2 เพราะมีรายละเอียดครบกว่า จึงควรคัดสำเนาที่นายทะเบียนรับรองใหม่ก่อนเริ่มขั้นตอนแปล",
      "เอกสารทะเบียนที่นำไปใช้ต่างประเทศควรเป็นฉบับคัดรับรองที่ออกใหม่ ไม่ใช่สำเนาถ่ายเอกสารเก่าที่เก็บไว้หลายปี เพราะบางประเทศกำหนดว่าเอกสารต้องออกไม่เกินกรอบเวลาหนึ่งก่อนยื่น กรอบเวลานี้ผันแปรตามหน่วยงานปลายทางและควรตรวจสอบจากประกาศของหน่วยงานนั้นโดยตรง",
      "ลำดับขั้นตอนสำคัญกว่าที่หลายคนคิด คำแปลต้องได้รับการรับรองจากกรมการกงสุลก่อน แล้วจึงนำไปให้สถานทูตของประเทศปลายทางรับรองต่อ หากข้ามลำดับ สถานทูตจะไม่รับรองให้และต้องเริ่มใหม่",
      "การถอดชื่อเป็นอักษรโรมันต้องสอดคล้องกับหนังสือเดินทางของทั้งสองฝ่าย หากในทะเบียนสมรสสะกดชื่อคู่สมรสต่างชาติไว้เป็นภาษาไทยตามเสียงอ่าน ผู้แปลต้องถอดกลับให้ตรงกับพาสปอร์ต ไม่ใช่ถอดตามเสียงอีกครั้ง มิฉะนั้นชื่อจะเพี้ยนจนหน่วยงานปลายทางถือว่าเป็นคนละคน",
      "เมื่ออนุสัญญาอะพอสทีลมีผลกับประเทศไทย เอกสารที่จะใช้ในประเทศภาคีจะใช้ตราอะพอสทีลแทนการรับรองซ้อนที่สถานทูตได้ แต่สำหรับประเทศที่ไม่ใช่ภาคี ลำดับการรับรองแบบเดิมยังคงใช้ต่อไป",
    ],
    bodyEn: [
      "Thailand issues two marriage documents that carry different weight: the marriage certificate (Kor.Ror. 3) handed to the couple, and the certified copy of the marriage register (Kor.Ror. 2). Many foreign authorities ask for the Kor.Ror. 2 because it carries fuller particulars, so request a freshly certified copy before starting the translation chain.",
      "Use a newly issued certified copy rather than a photocopy kept in a drawer for years: several countries require the civil-status document to have been issued within a set window before filing. That window varies by authority and should be checked against that authority's own published rule.",
      "Order matters more than people expect. The translation must be legalised by the Department of Consular Affairs first, and only then endorsed by the destination country's embassy. Skip a step and the embassy will refuse, sending you back to the start.",
      "Romanisation must follow both passports. Where the Thai register spells a foreign spouse's name phonetically in Thai script, the translator must restore the passport spelling rather than transliterate the sound again — otherwise the receiving authority reads it as a different person.",
      "Once the Apostille Convention is in force for Thailand, documents destined for other contracting states will carry an apostille instead of the double embassy endorsement. For non-contracting states the traditional chain continues to apply.",
    ],
    documentsTh: [
      "สำเนาทะเบียนสมรส (คร.2) ฉบับคัดรับรองจากสำนักทะเบียน",
      "ใบสำคัญการสมรส (คร.3)",
      "หนังสือเดินทางหรือบัตรประชาชนของคู่สมรสทั้งสองฝ่าย",
      "คำแปลเอกสารเป็นภาษาปลายทางหรือภาษาอังกฤษ",
      "หนังสือมอบอำนาจ กรณีให้ผู้อื่นดำเนินการแทน",
    ],
    documentsEn: [
      "Certified copy of the marriage register (Kor.Ror. 2) issued by the district office",
      "Marriage certificate (Kor.Ror. 3)",
      "Passports or ID cards of both spouses",
      "Translation into the destination language or English",
      "Power of attorney if an agent will file on your behalf",
    ],
    chainTh: [
      "คัดสำเนาทะเบียนสมรสฉบับรับรองใหม่จากสำนักทะเบียน",
      "แปลเอกสารเป็นภาษาปลายทางหรือภาษาอังกฤษ",
      "รับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
      "นำเอกสารที่รับรองแล้วไปรับรองต่อที่สถานทูตของประเทศปลายทาง (หรือใช้ตราอะพอสทีลเมื่ออนุสัญญามีผล สำหรับประเทศภาคี)",
      "ยื่นต่อหน่วยงานปลายทางตามรูปแบบที่หน่วยงานนั้นกำหนด",
    ],
    chainEn: [
      "Obtain a freshly certified copy of the marriage register",
      "Translate into the destination language or English",
      "Legalise the translation at the Legalisation Division, Department of Consular Affairs",
      "Have the destination embassy endorse the legalised document (or use an apostille once the Convention is in force, for contracting states)",
      "File with the receiving authority in the format it prescribes",
    ],
    checklistTh: [
      "ใช้ฉบับคัดรับรองใหม่ ไม่ใช่สำเนาถ่ายเอกสารเก่า",
      "ชื่ออักษรโรมันตรงกับหนังสือเดินทางของทั้งสองฝ่าย",
      "ลำดับการรับรอง: กรมการกงสุลก่อน แล้วจึงสถานทูต",
      "ตรวจว่าหน่วยงานปลายทางต้องการฉบับแปลภาษาอะไร",
      "ตรวจว่าต้องยื่นต้นฉบับหรือสำเนารับรองสำเนาถูกต้อง",
    ],
    checklistEn: [
      "Use a newly certified copy, not an old photocopy",
      "Latin-script names match both passports",
      "Correct order: Consular Affairs first, then the embassy",
      "Confirm which language the receiving authority requires",
      "Confirm whether originals or certified copies must be filed",
    ],
    faqsTh: [
      {
        q: "ต่างประเทศขอ คร.2 หรือ คร.3",
        a: "หน่วยงานส่วนใหญ่ขอสำเนาทะเบียนสมรส (คร.2) เพราะแสดงรายการทะเบียนครบถ้วน แต่บางแห่งขอทั้งสองฉบับ แนะนำให้เตรียมทั้งคู่และตรวจกับหน่วยงานปลายทางก่อน",
      },
      {
        q: "ทะเบียนสมรสเก่าหลายสิบปีใช้ได้ไหม",
        a: "ตัวการสมรสยังสมบูรณ์ แต่หน่วยงานปลายทางมักขอฉบับคัดรับรองที่ออกใหม่ จึงควรไปคัดสำเนารับรองจากสำนักทะเบียนก่อนเริ่มขั้นตอนแปลและรับรอง",
      },
      {
        q: "ต้องรับรองที่สถานทูตด้วยทุกครั้งหรือไม่",
        a: "ขึ้นกับข้อกำหนดของประเทศปลายทาง หลายประเทศกำหนดให้มีการรับรองซ้อนของสถานทูต แต่บางหน่วยงานรับเพียงการรับรองของกรมการกงสุล ควรตรวจสอบจากประกาศของหน่วยงานนั้นก่อน",
      },
      {
        q: "อะพอสทีลจะทำให้ขั้นตอนสั้นลงเมื่อไร",
        a: `อนุสัญญาอะพอสทีลจะมีผลกับประเทศไทยในวันที่ ${APOSTILLE_STATUS.entryIntoForceTh} ตามข้อมูลของ HCCH หลังจากนั้นเอกสารที่ใช้ในประเทศภาคีจะใช้ตราอะพอสทีลแทนการรับรองที่สถานทูตได้`,
      },
      {
        q: "สอบถามค่าบริการได้ทางไหน",
        a: "ติดต่อเจ้าหน้าที่ทางโทรศัพท์ LINE หรืออีเมล พร้อมแจ้งประเทศปลายทางและจำนวนเอกสาร เพื่อรับข้อมูลที่ตรงกับเคสของคุณ",
      },
    ],
    faqsEn: [
      {
        q: "Does the foreign authority want the Kor.Ror. 2 or the Kor.Ror. 3?",
        a: "Most ask for the certified copy of the register (Kor.Ror. 2) because it shows the full particulars; some want both. Prepare both and confirm with the receiving authority.",
      },
      {
        q: "Is a decades-old marriage certificate still usable?",
        a: "The marriage itself remains valid, but receiving authorities usually want a recently issued certified copy, so obtain one from the district office before starting translation and legalisation.",
      },
      {
        q: "Is embassy endorsement always required?",
        a: "Embassy endorsement is not always required for document legalization, as official requirements vary by destination country. While many overseas authorities demand a second embassy endorsement, certain organizations accept Department of Consular Affairs legalization alone. Applicants should always confirm the specific receiving authority's published guidelines before submitting their documents.",
      },
      {
        q: "When will the apostille shorten this?",
        a: `The Apostille Convention enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} according to HCCH. From then, documents used in contracting states can carry an apostille instead of the embassy endorsement.`,
      },
      {
        q: "How do I get a quote?",
        a: "Contact our team by phone, LINE or email with the destination country and the number of documents, and we will quote against your actual file.",
      },
    ],
  },
  {
    slug: "foreign-marriage-recorded-in-thailand",
    titleTh: "จดทะเบียนสมรสต่างประเทศ แล้วบันทึกฐานะแห่งครอบครัวในไทย (คร.22)",
    titleEn: "Recording a foreign marriage in Thailand (Kor.Ror. 22)",
    summaryTh:
      "คู่สมรสที่จดทะเบียนตามกฎหมายต่างประเทศและต้องการให้ทางทะเบียนไทยรับรู้ ต้องนำทะเบียนสมรสต่างประเทศมารับรอง แปล และยื่นบันทึกฐานะแห่งครอบครัว (คร.22) ที่สำนักทะเบียน",
    summaryEn:
      "Couples married under foreign law who need Thai records to reflect it: the foreign marriage certificate is authenticated, translated, and filed at a district office as a family-status record (Kor.Ror. 22).",
    bodyTh: [
      "การสมรสที่ทำถูกต้องตามกฎหมายของประเทศที่จดทะเบียน โดยหลักย่อมมีผลตามกฎหมาย แต่หน่วยงานไทยหลายแห่ง เช่น การเปลี่ยนคำนำหน้าและนามสกุล การขอวีซ่าและใบอนุญาตให้คู่สมรส หรือเรื่องมรดกและทรัพย์สิน ต้องอาศัยรายการทางทะเบียนของไทย จึงต้องมาบันทึกฐานะแห่งครอบครัว (คร.22) ที่สำนักทะเบียน",
      "เอกสารทะเบียนสมรสที่ออกในต่างประเทศต้องผ่านการรับรองตามระเบียบของประเทศที่ออกก่อน แล้วจึงให้สถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ของไทยในประเทศนั้นรับรอง เมื่อนำเข้ามาในไทยจึงแปลเป็นภาษาไทยและรับรองคำแปลที่กรมการกงสุลอีกชั้นหนึ่ง",
      "หลายเคสสะดุดที่ตัวสะกดชื่อภาษาไทยของคู่สมรสฝ่ายไทย เพราะชื่อในทะเบียนสมรสต่างประเทศมักเป็นอักษรโรมันตามพาสปอร์ต เมื่อแปลกลับเป็นไทยต้องใช้ชื่อไทยจริงตามทะเบียนบ้าน ไม่ใช่ถอดเสียงจากอักษรโรมัน มิฉะนั้นนายทะเบียนจะเชื่อมโยงกับบุคคลในทะเบียนราษฎรไม่ได้",
      "หากต้องการเปลี่ยนนามสกุลตามคู่สมรส เป็นคนละเรื่องกับการบันทึกฐานะแห่งครอบครัว และเป็นสิทธิที่กฎหมายให้เลือก ไม่ใช่หน้าที่บังคับ ควรวางลำดับให้ชัดว่าจะบันทึก คร.22 ก่อน แล้วจึงดำเนินการเรื่องนามสกุลและเอกสารประจำตัวที่ตามมา เพื่อไม่ให้ชื่อในเอกสารชุดต่าง ๆ ขัดกันเอง",
      "การหย่าตามกฎหมายต่างประเทศก็ใช้หลักเดียวกัน คือมาบันทึกฐานะแห่งครอบครัวกรณีหย่าที่สำนักทะเบียน เพื่อให้สถานภาพในทะเบียนไทยตรงกับความเป็นจริง ก่อนจะสมรสใหม่หรือดำเนินการอื่นที่อาศัยสถานภาพโสด",
    ],
    bodyEn: [
      "A marriage validly concluded under the law of the country where it was registered is in principle effective, but many Thai processes — changing title and surname, spouse visas and permits, inheritance and property matters — work off Thai civil records. That is why the marriage is recorded at a district office as a family-status record, the Kor.Ror. 22.",
      "The foreign marriage certificate must first be authenticated under the issuing country's rules, then endorsed by the Royal Thai Embassy or Consulate-General in that country. Back in Thailand it is translated into Thai and the translation legalised by the Department of Consular Affairs.",
      "Files often stall on the Thai spelling of the Thai spouse's name. Foreign certificates carry the Latin passport spelling; the Thai translation must restore the person's actual Thai name as it appears in the house registration, not transliterate the Latin back into Thai — otherwise the registrar cannot match the person to the civil registry.",
      "Taking the spouse's surname is a separate matter from recording the marriage, and Thai law treats it as a choice rather than an obligation. Sequence it deliberately: record the Kor.Ror. 22 first, then handle the surname and the identity documents that follow, so the names across your documents never contradict each other.",
      "A divorce granted abroad follows the same logic: record the family-status change at a district office so Thai records reflect reality before remarrying or doing anything else that depends on single status.",
    ],
    documentsTh: [
      "ทะเบียนสมรสหรือใบสำคัญการสมรสที่ออกโดยหน่วยงานต่างประเทศ ผ่านการรับรองตามระเบียบของประเทศนั้น",
      "การรับรองจากสถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทยในประเทศที่ออกเอกสาร",
      "คำแปลภาษาไทย พร้อมการรับรองคำแปลจากกรมการกงสุล",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านของฝ่ายไทย",
      "หนังสือเดินทางของคู่สมรสชาวต่างชาติ",
      "หลักฐานการสิ้นสุดการสมรสเดิมของทั้งสองฝ่าย (ถ้ามี)",
    ],
    documentsEn: [
      "Foreign marriage certificate, authenticated under the issuing country's own rules",
      "Endorsement by the Royal Thai Embassy or Consulate-General in the country of issue",
      "Thai translation, with the translation legalised by the Department of Consular Affairs",
      "Thai spouse's national ID card and house registration",
      "Foreign spouse's passport",
      "Evidence that any prior marriage of either party has ended (if applicable)",
    ],
    chainTh: [
      "รับรองทะเบียนสมรสต่างประเทศตามระเบียบของประเทศที่ออกเอกสาร",
      "ให้สถานทูตหรือสถานกงสุลใหญ่ไทยในประเทศนั้นรับรอง",
      "แปลเป็นภาษาไทยและรับรองคำแปลที่กรมการกงสุล",
      "ยื่นบันทึกฐานะแห่งครอบครัว (คร.22) ที่สำนักทะเบียนอำเภอหรือสำนักงานเขต",
      "ดำเนินการต่อเนื่อง เช่น เปลี่ยนคำนำหน้า/นามสกุล และปรับปรุงเอกสารประจำตัว (ถ้าประสงค์)",
    ],
    chainEn: [
      "Authenticate the foreign marriage certificate under the issuing country's rules",
      "Have the Royal Thai Embassy or Consulate-General in that country endorse it",
      "Translate into Thai and legalise the translation at the Department of Consular Affairs",
      "File the family-status record (Kor.Ror. 22) at a district or khet office",
      "Handle any follow-on steps such as title/surname change and updating identity documents, if desired",
    ],
    checklistTh: [
      "ชื่อไทยในคำแปลตรงกับทะเบียนบ้าน ไม่ใช่ถอดเสียงจากอักษรโรมัน",
      "มีตรารับรองของสถานทูตไทยในประเทศที่ออกเอกสาร",
      "คำแปลผ่านการรับรองของกรมการกงสุลแล้ว",
      "ตรวจสถานภาพเดิมของทั้งสองฝ่ายว่าปิดครบตามกฎหมาย",
      "วางลำดับก่อน–หลังระหว่าง คร.22 กับการเปลี่ยนนามสกุล",
    ],
    checklistEn: [
      "The Thai name in the translation matches the house registration, not a re-transliteration",
      "Endorsement by the Royal Thai Embassy in the issuing country is present",
      "The translation is already legalised by Consular Affairs",
      "Both parties' previous marital statuses are legally closed",
      "The order between the Kor.Ror. 22 and any surname change is planned",
    ],
    faqsTh: [
      {
        q: "ไม่บันทึก คร.22 จะถือว่าไม่ได้สมรสหรือไม่",
        a: "การสมรสที่ทำถูกต้องตามกฎหมายต่างประเทศไม่ได้หายไปเพราะไม่ได้บันทึก แต่หน่วยงานไทยที่ตรวจจากทะเบียนราษฎรจะยังเห็นสถานภาพเดิม ทำให้ติดขัดเมื่อต้องใช้สิทธิที่อาศัยสถานะคู่สมรส",
      },
      {
        q: "ต้องไปบันทึกที่อำเภอตามทะเบียนบ้านหรือไม่",
        a: "แนวปฏิบัติของแต่ละสำนักทะเบียนอาจต่างกัน บางแห่งรับเฉพาะกรณีที่เกี่ยวข้องกับพื้นที่ จึงควรสอบถามสำนักทะเบียนที่ตั้งใจจะไปยื่นก่อนเดินทาง",
      },
      {
        q: "หย่าที่ต่างประเทศต้องบันทึกในไทยไหม",
        a: "ควรบันทึกฐานะแห่งครอบครัวกรณีหย่า เพื่อให้สถานภาพในทะเบียนไทยตรงกับความจริง โดยเฉพาะหากจะสมรสใหม่หรือดำเนินการที่ต้องยืนยันสถานภาพ",
      },
      {
        q: "ใช้เวลานานเท่าไร",
        a: "กรอบเวลาผันแปรตามประเทศที่ออกเอกสารและคิวของแต่ละหน่วยงาน เราจึงไม่ระบุจำนวนวันตายตัว แต่จะช่วยตรวจสอบสถานะและวางลำดับให้เหมาะกับกำหนดของคุณ",
      },
    ],
    faqsEn: [
      {
        q: "If we never file the Kor.Ror. 22, are we unmarried?",
        a: "A marriage validly concluded abroad does not disappear, but Thai agencies reading the civil registry will still see the old status, which blocks anything that depends on spousal status.",
      },
      {
        q: "Must we file at the office where the Thai spouse is registered?",
        a: "Filing at the specific district office where the Thai spouse is registered depends on local administrative practice. Certain registration offices only accept filings connected directly to their geographic area, while others may allow broader submissions. Applicants are advised to contact the designated district office directly to confirm their local filing policy before traveling.",
      },
      {
        q: "Does a foreign divorce need recording in Thailand?",
        a: "A foreign divorce should be recorded in Thailand as an official family-status change to keep official Thai records accurate. Updating state records is essential to reflect personal status changes before remarrying, executing legal transactions, or completing official administrative procedures that require verified proof of marital status.",
      },
      {
        q: "How long does it take?",
        a: "Timing varies with the issuing country and each agency's caseload, so we do not publish a fixed number of days; we check current status and sequence the steps around your deadline.",
      },
    ],
  },
  {
    slug: "prenuptial-agreement",
    titleTh: "สัญญาก่อนสมรส (Prenuptial Agreement) ไทย–ต่างชาติ",
    titleEn: "Thai–foreign prenuptial agreements",
    summaryTh:
      "สัญญาก่อนสมรสตามกฎหมายไทยต้องทำเป็นหนังสือ ลงลายมือชื่อคู่สมรสและพยานสองคน และจดแจ้งไว้ในทะเบียนสมรสพร้อมกันในวันจดทะเบียน มิฉะนั้นตกเป็นโมฆะ",
    summaryEn:
      "Under Thai law a prenuptial agreement must be in writing, signed by both parties and two witnesses, and entered in the marriage register at the same time the marriage is registered — otherwise it is void.",
    bodyTh: [
      "ประมวลกฎหมายแพ่งและพาณิชย์กำหนดเงื่อนไขรูปแบบของสัญญาก่อนสมรสไว้เข้มงวด สัญญาต้องทำเป็นหนังสือ ลงลายมือชื่อคู่สมรสทั้งสองฝ่ายและพยานอย่างน้อยสองคน และต้องจดแจ้งข้อตกลงนั้นไว้ในทะเบียนสมรสพร้อมกับการจดทะเบียนสมรส หากไม่ครบเงื่อนไขนี้ สัญญาย่อมตกเป็นโมฆะ ไม่ว่าเนื้อหาจะเป็นธรรมเพียงใด",
      "ข้อจำกัดสำคัญคือสัญญาก่อนสมรสจัดการได้เฉพาะเรื่องทรัพย์สินระหว่างคู่สมรส และต้องไม่ขัดต่อความสงบเรียบร้อยหรือศีลธรรมอันดี รวมทั้งไม่อาจกำหนดให้ใช้กฎหมายต่างประเทศบังคับความสัมพันธ์ทางทรัพย์สินได้ตามอำเภอใจ ข้อตกลงเรื่องอำนาจปกครองบุตรหรือค่าอุปการะเลี้ยงดูบุตรในอนาคตไม่ใช่ขอบเขตที่สัญญาก่อนสมรสจะผูกมัดศาลได้",
      "คู่สมรสไทย–ต่างชาติจำนวนมากทำสัญญาสองภาษาในเอกสารฉบับเดียว เพื่อให้ทั้งสองฝ่ายเข้าใจข้อความที่ลงนามจริง และเพื่อให้นายทะเบียนอ่านฉบับภาษาไทยได้โดยตรง ควรระบุให้ชัดว่าหากถ้อยคำสองภาษาขัดกันให้ยึดฉบับใด",
      "จังหวะเวลาคือความเสี่ยงที่ควบคุมได้ง่ายที่สุดแต่ถูกมองข้ามบ่อยที่สุด สัญญาต้องพร้อมและนำไปด้วยในวันจดทะเบียน การกลับมาทำภายหลังไม่ใช่สัญญาก่อนสมรสอีกต่อไป และการแก้ไขข้อตกลงหลังสมรสมีข้อจำกัดตามกฎหมาย",
      "หากคู่สมรสมีทรัพย์สินหรือถิ่นที่อยู่ในหลายประเทศ ควรพิจารณาความสอดคล้องกับกฎหมายของประเทศที่ทรัพย์สินตั้งอยู่ด้วย เนื้อหาหน้านี้เป็นข้อมูลทั่วไปเพื่อการเตรียมเอกสาร ไม่ใช่คำแนะนำทางกฎหมายเฉพาะเรื่อง ควรปรึกษาทนายความสำหรับข้อเท็จจริงของคุณโดยเฉพาะ",
    ],
    bodyEn: [
      "The Civil and Commercial Code sets strict formalities for a prenuptial agreement: it must be in writing, signed by both parties and at least two witnesses, and entered in the marriage register at the same time the marriage is registered. Miss any of these and the agreement is void, however fair its content.",
      "Its scope is limited to property between the spouses, it must not offend public order or good morals, and the parties cannot freely elect a foreign law to govern their matrimonial property. Arrangements about future child custody or child maintenance are not matters a prenuptial agreement can bind a court on.",
      "Thai–foreign couples commonly execute a single bilingual instrument so that both parties genuinely understand what they sign and the registrar can read the Thai text directly. State clearly which language prevails if the two versions conflict.",
      "Timing is the easiest risk to control and the most often missed. The agreement must be finalised and physically presented on the registration day; coming back afterwards no longer produces a prenuptial agreement, and post-marriage variation is legally constrained.",
      "Where the couple holds assets or residence in several jurisdictions, consistency with the law where the assets sit also matters. This page is general information for document preparation, not advice on your specific matter — consult a lawyer about your own facts.",
    ],
    documentsTh: [
      "ร่างสัญญาก่อนสมรสฉบับสองภาษา (ไทย–อังกฤษ หรือภาษาที่คู่สมรสเข้าใจ)",
      "บัญชีทรัพย์สินและหนี้สินของแต่ละฝ่ายที่ต้องการระบุไว้",
      "บัตรประจำตัวประชาชน/หนังสือเดินทางของคู่สมรสทั้งสองฝ่าย",
      "พยานสองคนพร้อมบัตรประจำตัว",
      "เอกสารกรรมสิทธิ์ทรัพย์สินที่อ้างถึงในสัญญา (ถ้ามี)",
    ],
    documentsEn: [
      "Draft bilingual prenuptial agreement (Thai–English or a language both parties understand)",
      "Schedule of each party's assets and liabilities to be listed",
      "ID card / passports of both parties",
      "Two witnesses with identification",
      "Title documents for any property referred to in the agreement (if applicable)",
    ],
    chainTh: [
      "หารือขอบเขตข้อตกลงและตรวจสอบว่าอยู่ในกรอบที่กฎหมายไทยอนุญาต",
      "จัดทำร่างสองภาษาและตรวจความสอดคล้องของถ้อยคำทั้งสองฉบับ",
      "ทั้งสองฝ่ายอ่านและเข้าใจข้อความก่อนลงนาม (จัดล่ามหากจำเป็น)",
      "ลงนามพร้อมพยานสองคน ในวันเดียวกับที่ยื่นจดทะเบียนสมรส",
      "แจ้งนายทะเบียนให้จดแจ้งข้อตกลงไว้ในทะเบียนสมรส และเก็บสำเนาที่รับรองไว้",
    ],
    chainEn: [
      "Scope the agreement and confirm it stays within what Thai law permits",
      "Prepare the bilingual draft and reconcile the two language versions",
      "Ensure both parties read and understand the text before signing (arrange an interpreter if needed)",
      "Sign with two witnesses on the same day the marriage is registered",
      "Ask the registrar to enter the agreement in the marriage register and keep certified copies",
    ],
    checklistTh: [
      "สัญญาพร้อมและนำติดตัวไปในวันจดทะเบียนสมรส",
      "มีพยานลงลายมือชื่อครบสองคน",
      "นายทะเบียนจดแจ้งข้อตกลงไว้ในทะเบียนสมรสแล้ว",
      "เนื้อหาจำกัดอยู่ที่ทรัพย์สินระหว่างคู่สมรส",
      "ระบุชัดว่าฉบับภาษาใดใช้บังคับเมื่อถ้อยคำขัดกัน",
    ],
    checklistEn: [
      "The agreement is finalised and brought to the registration appointment",
      "Two witnesses have signed",
      "The registrar has entered it in the marriage register",
      "Content is confined to property between the spouses",
      "The prevailing language version is stated",
    ],
    faqsTh: [
      {
        q: "ทำสัญญาก่อนสมรสหลังจดทะเบียนแล้วได้ไหม",
        a: "ไม่ได้ในฐานะสัญญาก่อนสมรส เพราะกฎหมายกำหนดให้จดแจ้งไว้ในทะเบียนสมรสพร้อมกับการจดทะเบียน การตกลงเรื่องทรัพย์สินหลังสมรสมีข้อจำกัดตามกฎหมายและควรปรึกษาทนายความ",
      },
      {
        q: "สัญญาก่อนสมรสที่ทำในต่างประเทศใช้ในไทยได้หรือไม่",
        a: "การจะมีผลในระบบทะเบียนไทยต้องพิจารณาเงื่อนไขรูปแบบตามกฎหมายไทยประกอบด้วย โดยเฉพาะการจดแจ้งไว้ในทะเบียนสมรสของไทย จึงควรปรึกษาทนายความก่อนสรุปแนวทาง",
      },
      {
        q: "กำหนดเรื่องบุตรไว้ในสัญญาก่อนสมรสได้ไหม",
        a: "ข้อตกลงเรื่องอำนาจปกครองและค่าอุปการะเลี้ยงดูบุตรไม่ใช่ขอบเขตที่สัญญาก่อนสมรสจะผูกมัดศาล เพราะศาลพิจารณาโดยยึดประโยชน์สูงสุดของบุตรเป็นสำคัญ",
      },
      {
        q: "ต้องใช้พยานกี่คน",
        a: "อย่างน้อยสองคน และควรเป็นบุคคลที่ติดต่อได้ในภายหลัง เพื่อความสะดวกหากต้องยืนยันการลงนาม",
      },
    ],
    faqsEn: [
      {
        q: "Can we sign a prenuptial agreement after registering?",
        a: "Not as a prenuptial agreement — the law requires it to be entered in the marriage register at the time of registration. Post-marriage property arrangements are legally constrained; take legal advice.",
      },
      {
        q: "Is a prenup signed abroad effective in Thailand?",
        a: "Thai formal requirements still matter, particularly entry in the Thai marriage register. Take legal advice before relying on a foreign instrument.",
      },
      {
        q: "Can we settle child arrangements in the prenup?",
        a: "No. Custody and child maintenance are not matters a prenuptial agreement binds a court on; the court decides on the best interests of the child.",
      },
      {
        q: "How many witnesses are needed?",
        a: "Document signings require at least two witnesses present during the execution of legal instruments. These individuals should ideally be accessible contact persons who can easily be reached later if the document signing needs legal confirmation or identity verification by official authorities.",
      },
    ],
  },
  {
    slug: "spouse-visa-and-immigration-documents",
    titleTh: "เอกสารคู่สมรสสำหรับวีซ่าและตรวจคนเข้าเมือง",
    titleEn: "Spouse documents for visa and immigration filings",
    summaryTh:
      "ชุดเอกสารที่สถานทูตและสำนักงานตรวจคนเข้าเมืองมักเรียกดูเพื่อพิสูจน์ความสัมพันธ์: ทะเบียนสมรส ทะเบียนบ้าน หลักฐานความสัมพันธ์ และคำแปลที่รับรองแล้ว",
    summaryEn:
      "The bundle embassies and immigration offices ask for when a relationship must be proved: marriage registration, household records, relationship evidence and certified translations.",
    bodyTh: [
      "คำขอวีซ่าคู่สมรสและการต่ออายุการอยู่ในราชอาณาจักรตามเหตุผลสมรส ไม่ได้ตัดสินจากทะเบียนสมรสเพียงใบเดียว หน่วยงานพิจารณาว่าความสัมพันธ์เป็นจริงหรือไม่จากภาพรวมของเอกสาร ทั้งหลักฐานการอยู่ร่วมกัน การติดต่อสื่อสาร และหลักฐานทางการเงินตามที่หน่วยงานนั้นกำหนด",
      "หน้าที่ของเราอยู่ที่ความถูกต้องของเอกสารและคำแปล ไม่ใช่การรับประกันผลการพิจารณา เพราะดุลพินิจเป็นของเจ้าหน้าที่ผู้พิจารณาโดยตรง เราจะช่วยให้ชุดเอกสารสอดคล้องกัน ชื่อสะกดตรงกัน วันที่ไม่ขัดแย้งกัน และคำแปลผ่านการรับรองในรูปแบบที่หน่วยงานปลายทางยอมรับ",
      "ข้อกำหนดเรื่องหลักฐานทางการเงิน ระยะเวลาการถือครองเงินฝาก และรูปแบบหนังสือรับรองจากธนาคาร แตกต่างกันตามประเภทการตรวจลงตราและตามประกาศที่ใช้บังคับในขณะยื่น เราจึงไม่ระบุตัวเลขไว้ในหน้านี้ และแนะนำให้ยืนยันกับประกาศล่าสุดของหน่วยงานก่อนเตรียมเอกสาร",
      "เอกสารไทยที่จะใช้กับสถานทูตต่างประเทศต้องแปลและรับรองตามลำดับ ส่วนเอกสารต่างประเทศที่จะใช้กับหน่วยงานไทยต้องผ่านการรับรองจากประเทศต้นทางและสถานทูตไทย แล้วจึงแปลเป็นไทยและรับรองคำแปลที่กรมการกงสุล การสลับลำดับทำให้ต้องเริ่มใหม่",
      "จุดที่ควรระวังเป็นพิเศษคือความสม่ำเสมอของชื่อหลังการสมรส หากเปลี่ยนนามสกุลแล้วแต่ยังมีเอกสารบางฉบับใช้นามสกุลเดิม ควรแนบหลักฐานการเปลี่ยนชื่อไปพร้อมกันเสมอ เพื่อให้ผู้พิจารณาเชื่อมโยงเอกสารทุกฉบับเข้าเป็นบุคคลเดียวกันได้",
    ],
    bodyEn: [
      "Spouse visas and marriage-based extensions of stay are not decided on the marriage certificate alone. The authority assesses whether the relationship is genuine from the file as a whole: cohabitation evidence, communication history and whatever financial evidence that authority prescribes.",
      "Our responsibility is the accuracy of the documents and translations, not the outcome, which is entirely the deciding officer's discretion. We make the bundle internally consistent — names spelled identically, dates that do not contradict, translations certified in the form the receiving authority accepts.",
      "Financial thresholds, how long funds must be seasoned and the required format of bank letters differ by visa category and by the rules in force on the filing date, so this page publishes no figures. Verify against the authority's current notice before assembling the file.",
      "Thai documents going to a foreign embassy are translated and legalised in order; foreign documents going to a Thai authority are authenticated in the country of origin and by the Royal Thai Embassy, then translated into Thai and legalised by Consular Affairs. Reversing the order means starting again.",
      "Watch name consistency after marriage. If a surname changed but some documents still show the old one, always attach the change-of-name evidence so the officer can tie every document to one person.",
    ],
    documentsTh: [
      "ทะเบียนสมรส (คร.2) และใบสำคัญการสมรส (คร.3) พร้อมคำแปล",
      "ทะเบียนบ้านและบัตรประจำตัวประชาชนของคู่สมรสฝ่ายไทย",
      "หนังสือเดินทางของคู่สมรสชาวต่างชาติและหน้าตรวจลงตราที่เกี่ยวข้อง",
      "หลักฐานความสัมพันธ์ เช่น ภาพถ่าย บันทึกการติดต่อ หลักฐานการอยู่ร่วมกัน",
      "หลักฐานทางการเงินตามรูปแบบที่หน่วยงานปลายทางกำหนด",
      "หลักฐานการเปลี่ยนชื่อ–นามสกุล (ถ้ามี)",
    ],
    documentsEn: [
      "Marriage register (Kor.Ror. 2) and marriage certificate (Kor.Ror. 3) with translations",
      "Thai spouse's house registration and national ID card",
      "Foreign spouse's passport and relevant visa pages",
      "Relationship evidence: photographs, communication records, proof of cohabitation",
      "Financial evidence in the format the receiving authority prescribes",
      "Change-of-name evidence (if applicable)",
    ],
    chainTh: [
      "ตรวจรายการเอกสารกับประกาศล่าสุดของสถานทูตหรือสำนักงานตรวจคนเข้าเมืองที่จะยื่น",
      "คัดเอกสารทะเบียนฉบับรับรองใหม่จากสำนักทะเบียน",
      "แปลเอกสารเป็นภาษาที่หน่วยงานกำหนด",
      "รับรองคำแปลตามลำดับที่หน่วยงานนั้นยอมรับ (กรมการกงสุล และ/หรือ ทนายความผู้ทำคำรับรอง)",
      "จัดเรียงแฟ้มตามลำดับที่หน่วยงานระบุ พร้อมสำเนาสำรอง",
    ],
    chainEn: [
      "Check the list against the current notice of the embassy or immigration office you will file with",
      "Obtain freshly certified copies of the civil-status documents",
      "Translate into the language the authority requires",
      "Certify the translations in the order that authority accepts (Consular Affairs and/or a notarial services attorney)",
      "Assemble the file in the prescribed order with spare copies",
    ],
    checklistTh: [
      "ชื่อและนามสกุลตรงกันทุกฉบับ หรือมีหลักฐานการเปลี่ยนชื่อแนบ",
      "เอกสารทะเบียนเป็นฉบับคัดรับรองใหม่",
      "คำแปลอยู่ในรูปแบบที่หน่วยงานยอมรับ",
      "ตรวจข้อกำหนดการเงินจากประกาศล่าสุด ไม่ใช่จากข้อมูลบอกต่อ",
      "เตรียมสำเนาสำรองของทุกฉบับ",
    ],
    checklistEn: [
      "Names match across every document, or a change-of-name document is attached",
      "Civil-status documents are freshly certified copies",
      "Translations are in the accepted format",
      "Financial requirements verified from the current official notice, not hearsay",
      "Spare copies of everything prepared",
    ],
    faqsTh: [
      {
        q: "ทะเบียนสมรสอย่างเดียวพอสำหรับขอวีซ่าคู่สมรสไหม",
        a: "โดยทั่วไปไม่พอ หน่วยงานพิจารณาความสัมพันธ์จากภาพรวมของเอกสาร ทะเบียนสมรสเป็นเพียงจุดตั้งต้น ส่วนหลักฐานอื่นที่ต้องใช้ขึ้นกับประเภทการตรวจลงตราและประกาศที่ใช้บังคับ",
      },
      {
        q: "คำแปลต้องรับรองแบบไหน",
        a: "ขึ้นกับหน่วยงานปลายทาง บางแห่งรับคำแปลที่รับรองโดยกรมการกงสุล บางแห่งรับคำรับรองของทนายความผู้ทำคำรับรอง ควรตรวจสอบข้อกำหนดก่อนเริ่มแปลเพื่อไม่ต้องทำซ้ำ",
      },
      {
        q: "รับประกันว่าวีซ่าจะผ่านหรือไม่",
        a: "ไม่มีผู้ให้บริการรายใดรับประกันผลได้ เพราะการอนุมัติเป็นดุลพินิจของเจ้าหน้าที่ผู้พิจารณา เราช่วยให้เอกสารถูกต้อง ครบถ้วน และสอดคล้องกันที่สุดเท่านั้น",
      },
      {
        q: "ค่าใช้จ่ายเท่าไร",
        a: "ขึ้นกับจำนวนเอกสาร คู่ภาษา และขั้นตอนรับรองที่ต้องใช้ กรุณาสอบถามเจ้าหน้าที่ทางโทรศัพท์ LINE หรืออีเมล",
      },
    ],
    faqsEn: [
      {
        q: "Is the marriage certificate alone enough for a spouse visa?",
        a: "Usually not. The authority weighs the file as a whole; the certificate is the starting point, and what else is needed depends on the visa category and the rules in force.",
      },
      {
        q: "What kind of translation certification is required?",
        a: "The required translation certification depends on the specific receiving authority handling the document. Certain government agencies require legalisation by the Department of Consular Affairs, whereas other organisations accept certification performed by a qualified notarial services attorney. Document owners must confirm official criteria before proceeding with translation to prevent duplicate effort.",
      },
      {
        q: "Can you guarantee the visa?",
        a: "Visa approval cannot be guaranteed by any translation or legal service provider. Final decision authority remains entirely at the discretion of the reviewing immigration officer. Service providers focus on preparing visa documentation to ensure all submitted materials are accurate, complete, and consistent with official requirements.",
      },
      {
        q: "What does it cost?",
        a: "Service requirements depend on document count, language combinations, and the specific certification steps involved. Interested parties should contact our team directly via telephone, LINE messaging, or email to receive accurate details tailored to their exact document processing requirements.",
      },
    ],
  },
  {
    slug: "divorce-and-name-change",
    titleTh: "ทะเบียนหย่า การรับรองการหย่า และการเปลี่ยนชื่อ–นามสกุล",
    titleEn: "Divorce registration, recognition and name change",
    summaryTh:
      "เอกสารสิ้นสุดการสมรสและการเปลี่ยนชื่อ: ทะเบียนหย่า (คร.6/คร.7) คำพิพากษาศาล การบันทึกการหย่าที่ทำในต่างประเทศ และหนังสือสำคัญการเปลี่ยนชื่อที่ต้องใช้เชื่อมสายเอกสาร",
    summaryEn:
      "Documents that close a marriage and change a name: divorce registration (Kor.Ror. 6 / Kor.Ror. 7), court judgments, recording a foreign divorce, and the change-of-name certificates that keep a paper trail intact.",
    bodyTh: [
      "การหย่าตามกฎหมายไทยเกิดขึ้นได้สองทาง คือการหย่าโดยความยินยอมซึ่งจดทะเบียนที่สำนักทะเบียน และการหย่าโดยคำพิพากษาของศาล เอกสารผลลัพธ์ต่างกัน ทางแรกได้ทะเบียนการหย่าและใบสำคัญการหย่า ส่วนทางที่สองต้องใช้คำพิพากษาพร้อมหนังสือรับรองคดีถึงที่สุดประกอบการดำเนินการทางทะเบียน",
      "หน่วยงานต่างประเทศมักไม่รับใบสำคัญการหย่าเพียงใบเดียว แต่ขอสำเนาทะเบียนการหย่าที่แสดงรายละเอียดคู่กรณีและวันที่ ควรคัดฉบับรับรองใหม่จากสำนักทะเบียนก่อนเริ่มแปลและรับรอง เช่นเดียวกับเอกสารทะเบียนสมรส",
      "การหย่าที่เกิดขึ้นตามกฎหมายต่างประเทศ หากต้องการให้ทะเบียนไทยสะท้อนสถานภาพจริง ต้องนำเอกสารมารับรองที่ประเทศต้นทางและสถานทูตไทย แปลเป็นไทย รับรองคำแปลที่กรมการกงสุล แล้วจึงยื่นบันทึกฐานะแห่งครอบครัวที่สำนักทะเบียน กรณีคำพิพากษาของศาลต่างประเทศอาจมีเงื่อนไขเพิ่มเติมตามแนวปฏิบัติของสำนักทะเบียน ควรสอบถามก่อนยื่น",
      "การเปลี่ยนชื่อหรือนามสกุลหลังหย่า ทำให้เกิดช่องว่างระหว่างเอกสารเก่ากับเอกสารใหม่ทันที ทางแก้ที่หน่วยงานทุกแห่งยอมรับคือแนบหนังสือสำคัญการเปลี่ยนชื่อหรือเปลี่ยนนามสกุลไปพร้อมกันเสมอ เพื่อให้ผู้ตรวจเอกสารเห็นว่าเป็นบุคคลเดียวกันตลอดสายเอกสาร",
      "ก่อนสมรสใหม่ ควรตรวจว่าสถานภาพในทะเบียนราษฎรอัปเดตแล้วจริง เพราะหากระบบยังแสดงว่าสมรสอยู่ นายทะเบียนจะไม่รับจดทะเบียนสมรสครั้งใหม่ และการแก้ไขย้อนหลังใช้เวลามากกว่าการตรวจสอบล่วงหน้า",
    ],
    bodyEn: [
      "Thai law ends a marriage in two ways: divorce by mutual consent registered at a district office, and divorce by court judgment. The resulting paperwork differs — the first produces the divorce register and divorce certificate; the second requires the judgment together with a certificate that it is final before the civil registry acts on it.",
      "Foreign authorities rarely accept the divorce certificate alone; they ask for the certified copy of the divorce register showing the parties and dates. Obtain a freshly certified copy before translation and legalisation, exactly as with a marriage certificate.",
      "For a divorce granted abroad to be reflected in Thai records, the documents are authenticated in the country of origin and by the Royal Thai Embassy, translated into Thai, the translation legalised by Consular Affairs, and then filed as a family-status record at a district office. Foreign court judgments may attract additional conditions depending on the office's practice — ask before filing.",
      "Changing a name or surname after divorce instantly creates a gap between old and new documents. The remedy every authority accepts is to attach the change-of-name or change-of-surname certificate every time, so the reviewer can see one continuous identity.",
      "Before remarrying, verify that the civil registry actually shows the updated status. If the record still shows a subsisting marriage, the registrar will refuse the new registration, and correcting it afterwards takes far longer than checking in advance.",
    ],
    documentsTh: [
      "สำเนาทะเบียนการหย่า (คร.6) และใบสำคัญการหย่า (คร.7) ฉบับคัดรับรอง",
      "คำพิพากษาศาลและหนังสือรับรองคดีถึงที่สุด (กรณีหย่าโดยคำพิพากษา)",
      "เอกสารการหย่าที่ออกโดยหน่วยงานต่างประเทศ พร้อมการรับรองตามระเบียบและจากสถานทูตไทย",
      "หนังสือสำคัญการเปลี่ยนชื่อตัว/ชื่อสกุล (ถ้ามี)",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านฉบับปัจจุบัน",
    ],
    documentsEn: [
      "Certified copy of the divorce register (Kor.Ror. 6) and divorce certificate (Kor.Ror. 7)",
      "Court judgment and certificate of finality (for divorce by judgment)",
      "Foreign divorce documents, authenticated under local rules and by the Royal Thai Embassy",
      "Change-of-name / change-of-surname certificates (if applicable)",
      "Current national ID card and house registration",
    ],
    chainTh: [
      "คัดสำเนาทะเบียนการหย่าฉบับรับรองใหม่ หรือขอคัดคำพิพากษาพร้อมหนังสือรับรองคดีถึงที่สุด",
      "แปลเอกสารเป็นภาษาปลายทาง หรือแปลเป็นไทยกรณีเอกสารต่างประเทศ",
      "รับรองคำแปลที่กรมการกงสุล",
      "รับรองต่อที่สถานทูตปลายทาง หรือยื่นบันทึกฐานะแห่งครอบครัวกรณีหย่าที่สำนักทะเบียน",
      "ตรวจสอบว่าสถานภาพและชื่อในทะเบียนราษฎรอัปเดตครบก่อนดำเนินการอื่นต่อ",
    ],
    chainEn: [
      "Obtain a freshly certified copy of the divorce register, or the judgment plus certificate of finality",
      "Translate into the destination language, or into Thai for foreign documents",
      "Legalise the translation at the Department of Consular Affairs",
      "Have the destination embassy endorse it, or file the divorce family-status record at a district office",
      "Confirm the civil registry shows the updated status and name before taking further steps",
    ],
    checklistTh: [
      "ใช้ฉบับคัดรับรองใหม่ ไม่ใช่สำเนาถ่ายเอกสาร",
      "กรณีคำพิพากษา มีหนังสือรับรองคดีถึงที่สุดแนบ",
      "แนบหนังสือสำคัญการเปลี่ยนชื่อทุกครั้งที่ชื่อไม่ตรงกัน",
      "ตรวจสถานภาพในทะเบียนราษฎรก่อนวางแผนสมรสใหม่",
      "ตรวจว่าหน่วยงานปลายทางขอเอกสารฉบับใดโดยเฉพาะ",
    ],
    checklistEn: [
      "Use freshly certified copies, not photocopies",
      "For judgments, include the certificate of finality",
      "Attach change-of-name certificates whenever names differ",
      "Verify civil-registry status before planning a remarriage",
      "Confirm exactly which document the receiving authority wants",
    ],
    faqsTh: [
      {
        q: "หย่าที่ต่างประเทศแล้ว ต้องทำอะไรในไทยบ้าง",
        a: "ควรนำเอกสารการหย่าไปรับรองตามระเบียบของประเทศที่ออกและที่สถานทูตไทย แปลเป็นไทย รับรองคำแปลที่กรมการกงสุล แล้วยื่นบันทึกฐานะแห่งครอบครัวกรณีหย่าที่สำนักทะเบียน เพื่อให้ทะเบียนไทยแสดงสถานภาพตรงกับความจริง",
      },
      {
        q: "ใบสำคัญการหย่ากับสำเนาทะเบียนการหย่าต่างกันอย่างไร",
        a: "ใบสำคัญการหย่า (คร.7) เป็นใบที่มอบให้คู่หย่าถือไว้ ส่วนสำเนาทะเบียนการหย่า (คร.6) แสดงรายการทะเบียนโดยละเอียด หน่วยงานต่างประเทศมักขอฉบับหลัง",
      },
      {
        q: "เปลี่ยนนามสกุลกลับหลังหย่าต้องทำอย่างไรกับเอกสารเดิม",
        a: "เอกสารเดิมยังใช้ได้ แต่ต้องแนบหนังสือสำคัญการเปลี่ยนชื่อสกุลทุกครั้ง เพื่อให้ผู้ตรวจเชื่อมโยงได้ว่าเป็นบุคคลเดียวกัน และควรทยอยปรับปรุงเอกสารประจำตัวให้ตรงกัน",
      },
      {
        q: "ค่าบริการคิดอย่างไร",
        a: "ขึ้นกับชนิดและจำนวนเอกสาร คู่ภาษา และขั้นตอนรับรองที่ต้องใช้ กรุณาสอบถามเจ้าหน้าที่ทางโทรศัพท์ LINE หรืออีเมล",
      },
    ],
    faqsEn: [
      {
        q: "We divorced abroad — what must be done in Thailand?",
        a: "Authenticate the divorce documents under the issuing country's rules and at the Royal Thai Embassy, translate into Thai, legalise the translation at Consular Affairs, then file the divorce family-status record at a district office so Thai records match reality.",
      },
      {
        q: "What is the difference between the divorce certificate and the divorce register copy?",
        a: "The certificate (Kor.Ror. 7) is the document handed to the parties; the certified copy of the register (Kor.Ror. 6) shows the full particulars. Foreign authorities usually want the latter.",
      },
      {
        q: "I reverted to my maiden name — what about older documents?",
        a: "Older documents bearing a previous surname remain legally usable after reverting to a maiden name. Individuals must attach an official surname-change certificate whenever presenting older records so authorities can establish identity continuity. In addition, personal identity documents should be progressively updated over time to reflect the legal name change.",
      },
      {
        q: "How is the fee calculated?",
        a: "By document type and count, language pair and the certification steps required. Please contact our team by phone, LINE or email.",
      },
    ],
  },
];

export function getMarriageDocGroup(slug: string): MarriageDocGroup | undefined {
  return MARRIAGE_DOC_GROUPS.find((g) => g.slug === slug);
}
